import { useState, useMemo, useCallback, useRef } from 'react';
import { MARQUES_PHONAK_GROUP, MARQUES_OTICON_GROUP, MARQUES_WSA_GROUP, PREMIUM_COLOR_OPTION_KEY, isShoreAllowedForColor, getAvailableColors, getDefaultShoreForColor, getShoresForMaterial } from './orderFormConstants';

export function useOrderForm(products = [], initialData = null) {
  const [patientRef, setPatientRef] = useState('');
  const [patientType, setPatientTypeRaw] = useState('adulte');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [textureOreille, setTextureOreille] = useState('souple');
  const [fabricationType, setFabricationTypeRaw] = useState('embout_auditif');
  const [obturateurType, setObturateurType] = useState('');
  const [emboutType, setEmboutTypeRaw] = useState('ecouteur_deporte');
  const [marque, setMarqueRaw] = useState('');
  const [modele, setModele] = useState('');
  const [ecouteurVersion, setEcouteurVersion] = useState('');
  const [ecouteurPuissance, setEcouteurPuissance] = useState('');
  const [ecouteurType, setEcouteurType] = useState('');
  const [ecouteurPuissanceOticon, setEcouteurPuissanceOticon] = useState('');
  const [ecouteurInfoComplementaire, setEcouteurInfoComplementaire] = useState('');
  const [infoComplementaire, setInfoComplementaire] = useState('');
  const [formeEmbout, setFormeEmbout] = useState('');
  // Modifier options on top of the chosen forme. Demi-épaulement is only valid
  // with canule_epaulement; Évidée is valid for all formes. Can be combined.
  const [formeDemiEpaulement, setFormeDemiEpaulement] = useState(false);
  const [formeEvidee, setFormeEvidee] = useState(false);
  const [matiere, setMatiereRaw] = useState('resine_3d');
  // Wrapped: when switching matiere away from silicone, clear tube_lock montage.
  // Résines (3D / souple) → couleur forcée à transparent ; résine souple → dureté 70/90.
  // Silicone → dureté par défaut 40 et couleur remise en cohérence avec la matrice.
  const setMatiere = useCallback((v) => {
    setMatiereRaw(v);
    if (v !== 'silicone') {
      setMontageType((prev) => (prev === 'montage_tube_lock' ? 'montage_tube' : prev));
      setCouleur('transparent');
      setSelectedOptions((prev) => prev.filter(k => k !== PREMIUM_COLOR_OPTION_KEY));
    }
    if (v === 'resine_souple') {
      setDurete((prev) => (getShoresForMaterial('resine_souple').includes(prev) ? prev : '70'));
    }
    if (v === 'silicone') {
      setDurete((prev) => (getShoresForMaterial('silicone').includes(prev) ? prev : '40'));
    }
  }, []);
  const [durete, setDurete] = useState('40');
  const [couleur, setCouleur] = useState('transparent');
  const [etancheite, setEtancheite] = useState('sans');
  const [finition, setFinition] = useState('brillant');
  const [eventOD, setEventOD] = useState('Sans');
  const [eventOG, setEventOG] = useState('Sans');
  const [earSide, setEarSide] = useState('');
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [notes, setNotes] = useState('');
  const [fournirBushing, setFournirBushing] = useState(false);

  // Per-ear states for bilateral orders
  const [perEarDiff, setPerEarDiff] = useState(false);
  const [formeEmboutOG, setFormeEmboutOG] = useState('');
  const [formeDemiEpaulementOG, setFormeDemiEpaulementOG] = useState(false);
  const [formeEvideeOG, setFormeEvideeOG] = useState(false);
  const [matiereOG, setMatiereOGRaw] = useState('resine_3d');
  const [dureteOG, setDureteOG] = useState('40');
  // Wrapper matiere OG : symétrique à setMatiere (couleur unique pour les deux oreilles).
  const setMatiereOG = useCallback((v) => {
    setMatiereOGRaw(v);
    if (v !== 'silicone') {
      setCouleur('transparent');
      setSelectedOptions((prev) => prev.filter(k => k !== PREMIUM_COLOR_OPTION_KEY));
    }
    if (v === 'resine_souple') {
      setDureteOG((prev) => (getShoresForMaterial('resine_souple').includes(prev) ? prev : '70'));
    }
    if (v === 'silicone') {
      setDureteOG((prev) => (getShoresForMaterial('silicone').includes(prev) ? prev : '40'));
    }
  }, []);

  // Per-ear puissance for RIC bilateral
  const [ecouteurPuissanceOG, setEcouteurPuissanceOG] = useState('');
  const [ecouteurPuissanceOticonOG, setEcouteurPuissanceOticonOG] = useState('');

  // Groupe WSA (Signia / Rexton / Biotone / Widex) : écouteur 2.0 ou 3.0, puissance S/M/P
  const [ecouteurVersionWSA, setEcouteurVersionWSA] = useState('');
  const [ecouteurPuissanceWSA, setEcouteurPuissanceWSA] = useState('');
  const [ecouteurPuissanceWSAOG, setEcouteurPuissanceWSAOG] = useState('');

  // Montage type (radio selection per brand group)
  const [montageType, setMontageType] = useState('');

  // Obturateur-specific fields
  const [obturateurPoignee, setObturateurPoignee] = useState(false);
  const [obturateurCordon, setObturateurCordon] = useState(false);
  const [obturateurAttenuation, setObturateurAttenuation] = useState('');
  const [obturateurEtui, setObturateurEtui] = useState(false);
  const [obturateurForme, setObturateurForme] = useState('canule');
  // Sous-option "flottant" pour l'Anti-Eau : coché ⇒ dureté forcée 35 SH
  // (avec couleur appairée bleu/rouge), décoché ⇒ dureté forcée 40 SH.
  // La variante flottant est enregistrée en DB sous le lookup_key
  // `obturateur_anti_eau_flottant` (produit distinct), ce qui préserve les
  // statistiques historiques.
  const [obturateurFlottant, setObturateurFlottantRaw] = useState(false);

  // Finition anallergique (only when finition = brillant)
  const [finitionAnallergique, setFinitionAnallergique] = useState(false);

  // Marquage spécial (all types)
  const [marquageSpecial, setMarquageSpecial] = useState(false);
  const [marquageSpecialTexte, setMarquageSpecialTexte] = useState('');
  // Marquage image : mode = 'texte' | 'image'. Si mode=image → file_id stocké
  // dans marquageSpecialImageId après upload via POST /api/orders/upload-marquage-image.
  const [marquageSpecialMode, setMarquageSpecialMode] = useState('texte');
  const [marquageSpecialImageId, setMarquageSpecialImageId] = useState('');

  // Integral embout-specific fields (Phonak-only, écouteur intégré)
  const [integralCableLength, setIntegralCableLength] = useState('');

  // BTE brand-specific mini-tube option (independent of standard montage tube choice).
  // Available for Sonova group (Power SlimTube) and Demant group (Corda MiniFit 0.9/1.3 mm).
  const [bteBrandSpecificEnabled, setBteBrandSpecificEnabled] = useState(false);
  const [bteBrandSpecificType, setBteBrandSpecificType] = useState('');
  const [bteCordaMinifitDiameter, setBteCordaMinifitDiameter] = useState('');

  // Filter type used for SlimTip Link RIC montage and for Intégral embout. CeruStop or HF4.
  const [filtreType, setFiltreType] = useState('cerustop');

  // Bague 3DSA (Phonak RIC uniquement) : consentement client pour changement d'option
  // si la bague ne rentre pas dans le conduit + option de repli à appliquer par le tech.
  const [bague3dsaConsent, setBague3dsaConsent] = useState(false);
  const [bague3dsaFallback, setBague3dsaFallback] = useState('');

  // Conformateur d'oreille : description libre + photos illustratives.
  // Uniquement silicone transparent, duretés 25/35/40/60 SH, tarif fixe 43 € HT.
  const [conformateurDescription, setConformateurDescription] = useState('');
  const [conformateurPhotoIds, setConformateurPhotoIds] = useState([]);

  const isBilateral = earSide === 'both';

  const clearSpeakerInfo = useCallback(() => {
    setBague3dsaConsent(false);
    setBague3dsaFallback('');
    setEcouteurVersion('');
    setEcouteurPuissance('');
    setEcouteurType('');
    setEcouteurPuissanceOticon('');
    setEcouteurInfoComplementaire('');
    setEcouteurPuissanceOG('');
    setEcouteurPuissanceOticonOG('');
    setEcouteurVersionWSA('');
    setEcouteurPuissanceWSA('');
    setEcouteurPuissanceWSAOG('');
  }, []);

  // Miroirs (refs) pour lire la marque / le type d'embout courants dans des callbacks stables.
  const marqueRef = useRef('');
  const emboutTypeRef = useRef('');

  const setFabricationType = useCallback((v) => {
    setFabricationTypeRaw(v);
    setObturateurType('');
    setFormeEmbout('');
    setFormeEmboutOG('');
    if (v === 'obturateur') {
      setMatiere('silicone');
      setEmboutTypeRaw('ecouteur_deporte');
    }
    if (v === 'conformateur_oreille') {
      // Conformateur : uniquement silicone transparent avec dureté 25/35/40/60.
      // On force la matière, la couleur et une dureté par défaut valide.
      setMatiere('silicone');
      setMatiereOGRaw('silicone');
      setCouleur('transparent');
      setDurete((prev) => (['25', '40', '60'].includes(prev) ? prev : '40'));
      setDureteOG((prev) => (['25', '40', '60'].includes(prev) ? prev : '40'));
      setEmboutTypeRaw('ecouteur_deporte');
    } else {
      // Sortie de conformateur : on repart d'un état vierge côté description/photos
      setConformateurDescription('');
      setConformateurPhotoIds([]);
    }
    setSelectedOptions([]);
    setPerEarDiff(false);
    setMontageType('');
    setObturateurPoignee(false);
    setObturateurCordon(false);
    setObturateurAttenuation('');
    setObturateurEtui(false);
    setFinitionAnallergique(false);
    setMarquageSpecial(false);
    setMarquageSpecialTexte('');
    setMarquageSpecialMode('texte');
    setMarquageSpecialImageId('');
    // Retour sur « Embout auditif » avec un RIC et une marque déjà renseignée (défauts client) :
    // on réapplique le montage par défaut de la marque (ex. Phonak → Bague 3DSA + autorisation).
    if (v === 'embout_auditif' && emboutTypeRef.current === 'ecouteur_deporte' && marqueRef.current) {
      applyBrandMontageDefaults(marqueRef.current);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setEmboutType = useCallback((v) => {
    setEmboutTypeRaw(v);
    setMarqueRaw('');
    setModele('');
    clearSpeakerInfo();
    setFournirBushing(false);
    setMontageType('');
    setIntegralCableLength('');
    if (v === 'integral') {
      // Auto-set Phonak brand and force RIC-Phonak speaker fields
      setMarqueRaw('Phonak');
      setMontageType('slim_tip_link');
    }
    if (v === 'bte') {
      // Default BTE montage: tube lock for nourrissons (safer for very young patients),
      // classic tube otherwise.
      setMontageType(patientType === 'nourrisson' ? 'montage_tube_lock' : 'montage_tube');
      // For young patients, force silicone (safer + softer)
      if (patientType === 'enfant' || patientType === 'nourrisson') {
        setMatiereRaw('silicone');
      }
    }
    if (v === 'silk') {
      // Intra Silk = forme unique, résine souple obligatoire (70 SH par défaut)
      // L'embout a son propre tarif (47 €) — pas d'option supplément à cocher.
      setFormeEmbout('silk');
      setMatiereRaw('resine_souple');
      setDurete('70');
      setCouleur('transparent');
      setSelectedOptions(prev => prev.filter(k => k !== 'option_montage_intra_silk'));
    } else {
      // Si on quittait silk, on libère la forme sentinelle pour repartir d'un choix vierge
      setFormeEmbout(prev => prev === 'silk' ? '' : prev);
      setSelectedOptions(prev => prev.filter(k => k !== 'option_montage_intra_silk'));
    }
  }, [clearSpeakerInfo, patientType]);

  // Patient-type wrapper: applies the BTE+enfant/nourrisson → silicone rule
  // and pre-selects tube_lock for nourrisson, tube classique otherwise.
  const setPatientType = useCallback((v) => {
    setPatientTypeRaw(v);
    if (emboutType === 'bte') {
      if (v === 'enfant' || v === 'nourrisson') {
        setMatiereRaw('silicone');
      }
      // Only auto-toggle between the two BTE defaults; leave any other manual choice alone.
      setMontageType((prev) => {
        if (v === 'nourrisson' && prev === 'montage_tube') return 'montage_tube_lock';
        if (v !== 'nourrisson' && prev === 'montage_tube_lock') return 'montage_tube';
        return prev;
      });
    }
  }, [emboutType]);

  // Défauts de montage RIC par groupe de marque (réutilisés par setMarque et par le
  // retour sur « Embout auditif » quand une marque est déjà pré-remplie).
  const applyBrandMontageDefaults = useCallback((v) => {
    if (MARQUES_PHONAK_GROUP.includes(v)) {
      // Phonak RIC : Bague 3DSA par défaut, autorisation de changement précochée, repli Click tube
      setMontageType('montage_bague_3dsa');
      setBague3dsaConsent(true);
      setBague3dsaFallback('montage_tube');
      setEcouteurPuissance('M');
      setEcouteurPuissanceOG('M');
    } else if (MARQUES_OTICON_GROUP.includes(v)) {
      setMontageType('montage_bague_prodition');
      setEcouteurPuissanceOticon('65');
      setEcouteurPuissanceOticonOG('65');
    } else if (MARQUES_WSA_GROUP.includes(v)) {
      setMontageType('montage_bague_wsa');
      // Défauts pour WSA : écouteur 2.0, puissance M (S/M/P disponibles).
      setEcouteurVersionWSA('2.0');
      setEcouteurPuissanceWSA('M');
      setEcouteurPuissanceWSAOG('M');
    } else {
      setMontageType('montage_tube');
    }
  }, []);

  const setMarque = useCallback((v) => {
    setMarqueRaw(v);
    setModele('');
    clearSpeakerInfo();
    // For BTE, brand only enables brand-specific mini-tube options.
    // Do NOT override the user's BTE montage type (tube/tube_lock/tube_enfant).
    if (emboutType === 'bte') {
      return;
    }
    applyBrandMontageDefaults(v);
  }, [clearSpeakerInfo, emboutType, applyBrandMontageDefaults]);

  const setObturateurTypeWrapped = useCallback((v) => {
    setObturateurType(v);
    // Défauts : accessoires désactivés sauf pour la famille anti-eau (voir plus bas).
    setObturateurPoignee(false);
    setObturateurCordon(false);
    setObturateurAttenuation('');
    setObturateurEtui(false);
    setObturateurForme('canule');
    setObturateurFlottantRaw(false); // reset le drapeau flottant à chaque changement de type
    // Reset material/color/durete based on sub-type
    if (v === 'anti_eau') {
      // Anti-Eau (non flottant par défaut) : silicone 40 SH avec couleur
      // appairée bleu/rouge (Droite/Gauche). Poignée / cordon / étui cochés.
      setMatiere('silicone');
      setDurete('40');
      setCouleur('rouge_bleu');
      setObturateurPoignee(true);
      setObturateurCordon(true);
      setObturateurEtui(true);
    } else if (v === 'anti_eau_flottant') {
      // Chemin de compatibilité : les commandes historiques importées via
      // loadFromOrder passent d'abord par ici avant que loadFromOrder ne
      // convertisse en anti_eau + flottant=true.
      setMatiere('silicone');
      setDurete('35');
      setCouleur('rouge_bleu');
      setObturateurPoignee(true);
      setObturateurCordon(true);
      setObturateurEtui(true);
      setObturateurForme('fond_conque');
    } else if (v === 'anti_bruit_non_filtre') {
      setMatiere('silicone');
      setDurete('25');
      setCouleur('transparent');
      setObturateurForme('solide_plug_poignee');
    } else if (v === 'anti_bruit_filtre' || v === 'bouchon_musique') {
      setMatiere('silicone');
      setDurete('40');
      setCouleur('transparent');
    }
  }, []);

  // Wrapper flottant : uniquement significatif quand `obturateurType === 'anti_eau'`.
  // Coché ⇒ dureté 35 SH ; décoché ⇒ dureté 40 SH. Couleur appairée dans les deux cas.
  const setObturateurFlottant = useCallback((v) => {
    setObturateurFlottantRaw(!!v);
    if (v) {
      setDurete('35');
    } else {
      setDurete('40');
    }
    // Sécurise la couleur : dans les deux cas anti-eau reste en couleur appairée.
    setCouleur('rouge_bleu');
  }, []);

  // Wrapper dureté : si la couleur en cours n'existe pas dans la nouvelle dureté
  // (matrice couleur × dureté), on la remet sur la première couleur disponible.
  // Cas particulier existant : anti-bruit non filtré + rouge_bleu → uniquement 40 SH.
  const setDureteWrapped = useCallback((v) => {
    // Anti-Eau flottant : dureté verrouillée à 35 SH, ignore toute autre valeur.
    if (obturateurType === 'anti_eau_flottant') {
      setDurete('35');
      return;
    }
    setDurete(v);
    // Anti-bruit non filtré : la couleur appairée rouge_bleu n'existe qu'en 40 SH,
    // reset à transparent si l'utilisateur repasse à une autre dureté.
    if (obturateurType === 'anti_bruit_non_filtre' && v !== '40') {
      setCouleur((prev) => (prev === 'rouge_bleu' ? 'transparent' : prev));
    }
    // Couleur indisponible dans la nouvelle dureté → première couleur disponible.
    setCouleur((prev) => (isShoreAllowedForColor(prev, v) ? prev : (getAvailableColors([v])[0] || 'transparent')));
    // Contexte "paired-only" (obturateur en 40 SH) : rouge/bleu standalone deviennent
    // interdits — on bascule automatiquement sur la variante appairée rouge_bleu.
    if (fabricationType === 'obturateur' && v === '40') {
      setCouleur((prev) => ((prev === 'rouge' || prev === 'bleu') ? 'rouge_bleu' : prev));
    }
  }, [obturateurType, fabricationType]);

  // Wrapper dureté OG (bilatéral) : la couleur est unique pour les deux oreilles,
  // elle doit rester disponible dans les deux duretés.
  const setDureteOGWrapped = useCallback((v) => {
    setDureteOG(v);
    setCouleur((prev) => (isShoreAllowedForColor(prev, v) ? prev : (getAvailableColors([durete, v])[0] || 'transparent')));
  }, [durete]);

  // Wrapper couleur : si l'utilisateur choisit une couleur indisponible dans la
  // dureté silicone courante (OD et/ou OG), on bascule la dureté sur une valeur compatible.
  // Ceci s'applique aux embouts auditifs en silicone ET aux obturateurs.
  const setCouleurWrapped = useCallback((v) => {
    setCouleur(v);
    const target = getDefaultShoreForColor(v);
    if (target) {
      setDurete((prev) => (isShoreAllowedForColor(v, prev) ? prev : target));
      setDureteOG((prev) => (isShoreAllowedForColor(v, prev) ? prev : target));
    }
  }, []);

  const loadFromOrder = useCallback((order) => {
    if (!order) return;
    const opts = order.options || {};
    setPatientRef(order.patient_reference || '');
    setPatientType(opts.patient_type || 'adulte');
    setDeliveryDate(opts.delivery_date || '');
    setTextureOreille(opts.texture_oreille || 'souple');
    setFabricationTypeRaw(opts.fabrication_type || 'embout_auditif');
    // Compat descendante : les commandes antérieures stockent
    // obturateur_type='anti_eau_flottant'. On les recharge comme
    // anti_eau + drapeau flottant coché.
    if (opts.obturateur_type === 'anti_eau_flottant') {
      setObturateurType('anti_eau');
      setObturateurFlottantRaw(true);
    } else {
      setObturateurType(opts.obturateur_type || '');
      setObturateurFlottantRaw(!!opts.obturateur_flottant);
    }
    setEmboutTypeRaw(opts.embout_type || 'ecouteur_deporte');
    setMarqueRaw(opts.marque || '');
    setModele(opts.modele || '');
    setEcouteurVersion(opts.ecouteur_version || '');
    setEcouteurPuissance(opts.ecouteur_puissance || opts.ecouteur_puissance_od || '');
    setEcouteurType(opts.ecouteur_type || '');
    setEcouteurPuissanceOticon(opts.ecouteur_puissance_oticon || opts.ecouteur_puissance_oticon_od || '');
    setEcouteurInfoComplementaire('');
    setInfoComplementaire(opts.info_complementaire || '');
    // Demi-épaulement = forme à part entière (canule_demi_epaulement).
    // Compat : anciennes commandes avec le flag forme_demi_epaulement(_od) sur canule_epaulement.
    let odForme = opts.forme_embout_od || opts.forme_embout || '';
    let evideeOd = !!(opts.forme_evidee_od ?? opts.forme_evidee);
    if (odForme === 'canule_epaulement' && (opts.forme_demi_epaulement_od ?? opts.forme_demi_epaulement)) odForme = 'canule_demi_epaulement';
    if (odForme === 'canule_evidee') { odForme = 'canule_epaulement'; evideeOd = true; }
    setFormeEmbout(odForme);
    setFormeDemiEpaulement(false);
    setFormeEvidee(evideeOd);
    setMatiere(order.material || opts.matiere_od || 'resine_3d');
    setDurete(opts.durete || opts.durete_od || '40');
    // Sanitisation : les résines sont toujours transparentes ; une couleur
    // indisponible pour la dureté (matrice) est remise sur la première disponible.
    const rawColor = order.color || 'transparent';
    const matOd = order.material || opts.matiere_od || 'resine_3d';
    const matOg = opts.matiere_og || matOd;
    const bothSilicone = matOd === 'silicone' && matOg === 'silicone';
    setCouleur(!bothSilicone && rawColor !== 'transparent' ? 'transparent' : rawColor);
    setEtancheite(opts.etancheite || 'sans');
    setFinition(opts.finition || 'brillant');
    setEventOD(opts.event_od || 'Sans');
    setEventOG(opts.event_og || 'Sans');
    setEarSide(order.ear_side || '');
    setNotes(order.notes || '');
    setFournirBushing(order.fournir_bushing_cerustop || opts.fournir_bushing_cerustop || false);
    setMontageType(opts.montage_type || '');
    setObturateurPoignee(opts.obturateur_poignee || false);
    setObturateurCordon(opts.obturateur_cordon || false);
    setObturateurAttenuation(opts.obturateur_attenuation || '');
    setObturateurEtui(opts.obturateur_etui || false);
    setObturateurForme(opts.obturateur_forme || 'canule');
    setFinitionAnallergique(opts.finition_anallergique || false);
    setMarquageSpecial(opts.marquage_special || false);
    setMarquageSpecialTexte(opts.marquage_special_texte || '');
    setMarquageSpecialMode(opts.marquage_special_image_id ? 'image' : 'texte');
    setMarquageSpecialImageId(opts.marquage_special_image_id || '');
    setIntegralCableLength(opts.integral_cable_length || '');
    setBteBrandSpecificEnabled(opts.bte_brand_specific_enabled || false);
    setBteBrandSpecificType(opts.bte_brand_specific_type || '');
    setBteCordaMinifitDiameter(opts.bte_corda_minifit_diameter || '');
    setFiltreType(opts.filtre_type || 'cerustop');
    setBague3dsaConsent(!!opts.bague_3dsa_consent);
    setBague3dsaFallback(opts.bague_3dsa_fallback || '');
    // Groupe WSA
    setEcouteurVersionWSA(opts.ecouteur_version_wsa || '');
    setEcouteurPuissanceWSA(opts.ecouteur_puissance_wsa || opts.ecouteur_puissance_wsa_od || '');
    setEcouteurPuissanceWSAOG(opts.ecouteur_puissance_wsa_og || '');
    const optKeys = (order.selected_options || []).map(o => o.lookup_key).filter(Boolean);
    setSelectedOptions(optKeys.filter(k => k !== 'option_finition_mat_brillant'));

    // Per-ear data
    if (opts.forme_embout_og || opts.matiere_og) {
      setPerEarDiff(true);
      let ogForme = opts.forme_embout_og || opts.forme_embout || '';
      let evideeOg = !!opts.forme_evidee_og;
      if (ogForme === 'canule_epaulement' && opts.forme_demi_epaulement_og) ogForme = 'canule_demi_epaulement';
      if (ogForme === 'canule_evidee') { ogForme = 'canule_epaulement'; evideeOg = true; }
      setFormeEmboutOG(ogForme);
      setFormeDemiEpaulementOG(false);
      setFormeEvideeOG(evideeOg);
      setMatiereOG(opts.matiere_og || order.material || 'resine_3d');
      setDureteOG(opts.durete_og || '40');
    } else {
      setPerEarDiff(false);
      setFormeDemiEpaulementOG(false);
      setFormeEvideeOG(false);
    }
    setEcouteurPuissanceOG(opts.ecouteur_puissance_og || '');
    setEcouteurPuissanceOticonOG(opts.ecouteur_puissance_oticon_og || '');
    // Conformateur d'oreille
    setConformateurDescription(opts.conformateur_description || '');
    setConformateurPhotoIds(Array.isArray(opts.conformateur_photo_ids) ? opts.conformateur_photo_ids : []);
  }, []);

  const resolvedProductKey = useMemo(() => {
    if (fabricationType === 'obturateur') {
      if (!obturateurType) return null;
      // Variante flottant : produit distinct au catalogue → même prix mais key
      // différente pour analytics et compatibilité descendante.
      if (obturateurType === 'anti_eau' && obturateurFlottant) {
        return 'obturateur_anti_eau_flottant';
      }
      return `obturateur_${obturateurType}`;
    }
    if (fabricationType === 'conformateur_oreille') {
      // Produit unique — aucun sous-choix de forme.
      return 'conformateur_oreille';
    }
    if (!formeEmbout || !matiere) return null;
    // Résine souple : même produit/tarif que la résine 3D (la matière réelle est stockée dans `material`).
    return `embout_${formeEmbout}_${matiere === 'resine_souple' ? 'resine_3d' : matiere}`;
  }, [fabricationType, obturateurType, obturateurFlottant, formeEmbout, matiere]);

  const resolvedProduct = useMemo(() => {
    if (!resolvedProductKey) return null;
    return products.find(p => p.lookup_key === resolvedProductKey) || null;
  }, [resolvedProductKey, products]);

  const availableOptions = useMemo(() => {
    if (fabricationType === 'obturateur') {
      return products.filter(p => p.category === 'option_obturateur');
    }
    if (fabricationType === 'conformateur_oreille') {
      // Conformateur = tarif tout inclus, aucune option supplémentaire.
      return [];
    }
    const opts = products.filter(p => p.category === 'option_embout');
    return opts.filter(opt => {
      if (opt.lookup_key === 'option_finition_mat_brillant') return false;
      // Hide montage options — handled by radio now
      if (opt.lookup_key === 'option_montage_slim_tip_link') return false;
      if (opt.lookup_key === 'option_montage_bague') return false;
      if (opt.lookup_key === 'option_montage_click') return false;
      if (opt.lookup_key === 'option_montage_cerustop') return false;
      // Plus de supplément couleur : l'option historique n'est jamais proposée.
      if (opt.lookup_key === PREMIUM_COLOR_OPTION_KEY) return false;

      if (emboutType === 'bte') {
        return opt.lookup_key === 'option_montage_tube_bte';
      }
      if (emboutType === 'ecouteur_deporte') {
        if (opt.lookup_key === 'option_montage_tube_bte') return false;
        if (opt.lookup_key === 'option_montage_intra_silk') return false;
        return true;
      }
      if (emboutType === 'silk') {
        // Intra Silk : forme unique avec tarif tout inclus → aucune option supplémentaire
        return false;
      }
      return true;
    });
  }, [fabricationType, emboutType, products, matiere, matiereOG, isBilateral, perEarDiff]);

  const toggleOption = useCallback((lookupKey) => {
    setSelectedOptions(prev => {
      const next = prev.includes(lookupKey) ? prev.filter(k => k !== lookupKey) : [...prev, lookupKey];
      if (lookupKey === 'option_montage_click' && !next.includes('option_montage_click')) {
        setFournirBushing(false);
      }
      return next;
    });
  }, []);

  const selectedOptionProducts = useMemo(() => {
    return products.filter(p => selectedOptions.includes(p.lookup_key));
  }, [selectedOptions, products]);

  const totalPrice = useMemo(() => {
    let total = resolvedProduct ? resolvedProduct.base_price : 0;
    selectedOptionProducts.forEach(opt => { total += opt.base_price; });
    return total;
  }, [resolvedProduct, selectedOptionProducts]);

  const buildFormData = useCallback(() => {
    let ecouteurInfo = '';
    if (marque && MARQUES_PHONAK_GROUP.includes(marque)) {
      if (isBilateral) {
        ecouteurInfo = `Version ${ecouteurVersion} - OD: ${ecouteurPuissance} / OG: ${ecouteurPuissanceOG}`;
      } else {
        ecouteurInfo = `Version ${ecouteurVersion} - Puissance ${ecouteurPuissance}`;
      }
    } else if (marque && MARQUES_OTICON_GROUP.includes(marque)) {
      if (isBilateral) {
        ecouteurInfo = `${ecouteurType} - OD: ${ecouteurPuissanceOticon} / OG: ${ecouteurPuissanceOticonOG}`;
      } else {
        ecouteurInfo = `${ecouteurType} - Puissance ${ecouteurPuissanceOticon}`;
      }
    } else if (marque && MARQUES_WSA_GROUP.includes(marque)) {
      // Groupe WSA : combine version écouteur (2.0/3.0) + puissance (S/M/P).
      if (isBilateral) {
        ecouteurInfo = `Écouteur ${ecouteurVersionWSA} - OD: ${ecouteurPuissanceWSA} / OG: ${ecouteurPuissanceWSAOG}`;
      } else {
        ecouteurInfo = `Écouteur ${ecouteurVersionWSA} - Puissance ${ecouteurPuissanceWSA}`;
      }
    } else if (marque) {
      ecouteurInfo = ecouteurInfoComplementaire;
    }

    const isObt = fabricationType === 'obturateur';
    const isConformateur = fabricationType === 'conformateur_oreille';
    // "isMinimal" = conformateur ou obturateur : pas de contexte aide auditive
    const isMinimal = isObt || isConformateur;
    // Résine souple : non laquée (pas de finition / anallergique) et pas de marquage spécial
    // (seule la gravure du numéro de série est présente).
    const isResineSouple = !isMinimal && (matiere === 'resine_souple' || (isBilateral && perEarDiff && matiereOG === 'resine_souple'));
    // Groupe de marque actif : seuls ses champs puissance sont renseignés (évite qu'une
    // valeur Phonak résiduelle « M » soit affichée pour un écouteur Signia « 3.0 P »).
    const isPhonakGrp = !isMinimal && MARQUES_PHONAK_GROUP.includes(marque);
    const isOticonGrp = !isMinimal && MARQUES_OTICON_GROUP.includes(marque);
    const isWSAGrp = !isMinimal && MARQUES_WSA_GROUP.includes(marque);

    const formOptions = {
      patient_type: patientType,
      delivery_date: deliveryDate,
      texture_oreille: textureOreille,
      fabrication_type: fabricationType,
      // Persistance : la variante flottante est stockée comme
      // obturateur_type = 'anti_eau_flottant' pour préserver les stats et la
      // compat avec les commandes existantes.
      obturateur_type: (obturateurType === 'anti_eau' && obturateurFlottant) ? 'anti_eau_flottant' : obturateurType,
      obturateur_flottant: obturateurType === 'anti_eau' && obturateurFlottant,
      embout_type: isMinimal ? '' : emboutType,
      // Hearing-aid-specific fields — left empty on obturateur/conformateur orders so they never leak into the recap
      marque: isMinimal ? '' : marque,
      modele: isMinimal ? '' : modele,
      ecouteur_version: isPhonakGrp ? ecouteurVersion : '',
      ecouteur_puissance: isPhonakGrp ? ecouteurPuissance : '',
      ecouteur_type: isOticonGrp ? ecouteurType : '',
      ecouteur_puissance_oticon: isOticonGrp ? ecouteurPuissanceOticon : '',
      // Groupe WSA : version et puissance dédiées
      ecouteur_version_wsa: isWSAGrp ? ecouteurVersionWSA : '',
      ecouteur_puissance_wsa: isWSAGrp ? ecouteurPuissanceWSA : '',
      ecouteur_info: isMinimal ? '' : ecouteurInfo,
      info_complementaire: infoComplementaire,
      forme_embout: isMinimal ? '' : formeEmbout,
      // Modifier flags (only valid for embout_auditif, never for obturateur/conformateur)
      forme_demi_epaulement: fabricationType === 'embout_auditif' && formeEmbout === 'canule_demi_epaulement',
      forme_evidee: fabricationType === 'embout_auditif' ? formeEvidee : false,
      durete, etancheite,
      // Only send event for the actually-ordered side(s) — never for obturateurs/conformateurs
      event_od: isMinimal ? 'Sans' : ((earSide === 'right' || earSide === 'both') ? eventOD : 'Sans'),
      event_og: isMinimal ? 'Sans' : ((earSide === 'left' || earSide === 'both') ? eventOG : 'Sans'),
      finition: (isMinimal || isResineSouple) ? '' : finition,
      fournir_bushing_cerustop: isMinimal ? false : fournirBushing,
      montage_type: isMinimal ? '' : montageType,
      obturateur_poignee: obturateurPoignee,
      obturateur_cordon: obturateurCordon,
      obturateur_attenuation: obturateurAttenuation,
      obturateur_etui: obturateurEtui,
      // Anti-eau (flottant compris) : toujours fond de conque
      obturateur_forme: obturateurType === 'anti_eau' ? 'fond_conque' : obturateurForme,
      finition_anallergique: (isMinimal || isResineSouple) ? false : finitionAnallergique,
      marquage_special: (isConformateur || isResineSouple) ? false : marquageSpecial,
      marquage_special_texte: !isConformateur && marquageSpecial && marquageSpecialMode === 'texte' ? marquageSpecialTexte : '',
      marquage_special_image_id: !isConformateur && marquageSpecial && marquageSpecialMode === 'image' ? marquageSpecialImageId : '',
      integral_cable_length: (!isMinimal && emboutType === 'integral') ? integralCableLength : '',
      // BTE brand-specific mini-tube option (only when BTE + relevant brand group + checkbox enabled)
      bte_brand_specific_enabled: (!isMinimal && emboutType === 'bte') ? bteBrandSpecificEnabled : false,
      bte_brand_specific_type: (!isMinimal && emboutType === 'bte' && bteBrandSpecificEnabled) ? bteBrandSpecificType : '',
      bte_corda_minifit_diameter: (!isMinimal && emboutType === 'bte' && bteBrandSpecificEnabled && bteBrandSpecificType === 'corda_minifit') ? bteCordaMinifitDiameter : '',
      // Filter type for SlimTip Link RIC montage and Intégral embout
      filtre_type: (!isMinimal && ((emboutType === 'ecouteur_deporte' && montageType === 'slim_tip_link') || emboutType === 'integral')) ? filtreType : '',
      // Bague 3DSA (Phonak RIC only) : consent client + option de repli
      bague_3dsa_consent: (!isMinimal && emboutType === 'ecouteur_deporte' && montageType === 'montage_bague_3dsa') ? bague3dsaConsent : false,
      bague_3dsa_fallback: (!isMinimal && emboutType === 'ecouteur_deporte' && montageType === 'montage_bague_3dsa' && bague3dsaConsent) ? bague3dsaFallback : '',
      // Conformateur d'oreille : description libre + photos illustratives
      conformateur_description: isConformateur ? conformateurDescription : '',
      conformateur_photo_ids: isConformateur ? conformateurPhotoIds : [],
    };

    // Per-ear data for bilateral — never for obturateurs/conformateurs (no hearing aid context)
    if (isBilateral && !isMinimal) {
      formOptions.ecouteur_puissance_od = isPhonakGrp ? ecouteurPuissance : '';
      formOptions.ecouteur_puissance_og = isPhonakGrp ? ecouteurPuissanceOG : '';
      formOptions.ecouteur_puissance_oticon_od = isOticonGrp ? ecouteurPuissanceOticon : '';
      formOptions.ecouteur_puissance_oticon_og = isOticonGrp ? ecouteurPuissanceOticonOG : '';
      // WSA bilatéral : puissance différenciée OD/OG (version unique)
      formOptions.ecouteur_puissance_wsa_od = isWSAGrp ? ecouteurPuissanceWSA : '';
      formOptions.ecouteur_puissance_wsa_og = isWSAGrp ? ecouteurPuissanceWSAOG : '';

      if (perEarDiff) {
        formOptions.forme_embout_od = formeEmbout;
        formOptions.forme_embout_og = formeEmboutOG;
        formOptions.forme_demi_epaulement_od = formeEmbout === 'canule_demi_epaulement';
        formOptions.forme_demi_epaulement_og = formeEmboutOG === 'canule_demi_epaulement';
        formOptions.forme_evidee_od = formeEvidee;
        formOptions.forme_evidee_og = formeEvideeOG;
        formOptions.matiere_od = matiere;
        formOptions.matiere_og = matiereOG;
        formOptions.durete_od = durete;
        formOptions.durete_og = dureteOG;
      }
    }

    let allSelectedOptions = [...selectedOptions];
    if (finition && fabricationType === 'embout_auditif') {
      if (!allSelectedOptions.includes('option_finition_mat_brillant')) {
        allSelectedOptions.push('option_finition_mat_brillant');
      }
    }

    // Plus de supplément couleur (toutes les couleurs au même prix) — on retire
    // toute trace de l'ancienne option premium.
    allSelectedOptions = allSelectedOptions.filter(k => k !== PREMIUM_COLOR_OPTION_KEY);

    return {
      patient_reference: patientRef,
      ear_side: earSide,
      product_lookup_key: resolvedProductKey,
      selected_option_keys: allSelectedOptions,
      embout_type: fabricationType === 'obturateur' ? ((obturateurType === 'anti_eau' && obturateurFlottant) ? 'obturateur_anti_eau_flottant' : `obturateur_${obturateurType}`) : (fabricationType === 'conformateur_oreille' ? 'conformateur_oreille' : formeEmbout),
      material: matiere,
      color: couleur,
      options: formOptions,
      notes: notes || null,
      fournir_bushing_cerustop: fournirBushing,
      delivery_date: deliveryDate || null,
    };
  }, [patientRef, patientType, deliveryDate, textureOreille, fabricationType, obturateurType, obturateurFlottant, emboutType, marque, modele, ecouteurVersion, ecouteurPuissance, ecouteurType, ecouteurPuissanceOticon, ecouteurInfoComplementaire, infoComplementaire, formeEmbout, formeDemiEpaulement, formeEvidee, matiere, durete, couleur, etancheite, finition, eventOD, eventOG, earSide, selectedOptions, notes, resolvedProductKey, isBilateral, perEarDiff, formeEmboutOG, formeDemiEpaulementOG, formeEvideeOG, matiereOG, dureteOG, ecouteurPuissanceOG, ecouteurPuissanceOticonOG, fournirBushing, montageType, obturateurPoignee, obturateurCordon, obturateurAttenuation, obturateurEtui, obturateurForme, finitionAnallergique, marquageSpecial, marquageSpecialTexte, marquageSpecialMode, marquageSpecialImageId, integralCableLength, bteBrandSpecificEnabled, bteBrandSpecificType, bteCordaMinifitDiameter, filtreType, bague3dsaConsent, bague3dsaFallback, ecouteurVersionWSA, ecouteurPuissanceWSA, ecouteurPuissanceWSAOG, conformateurDescription, conformateurPhotoIds]);

  const reset = useCallback(() => {
    setPatientRef('');
    setPatientType('adulte');
    setDeliveryDate('');
    setTextureOreille('souple');
    setFabricationTypeRaw('embout_auditif');
    setObturateurType('');
    setEmboutTypeRaw('ecouteur_deporte');
    setMarqueRaw('');
    setModele('');
    clearSpeakerInfo();
    setInfoComplementaire('');
    setFormeEmbout('');
    setFormeDemiEpaulement(false);
    setFormeEvidee(false);
    setMatiere('resine_3d');
    setDurete('40');
    setCouleur('transparent');
    setEtancheite('sans');
    setFinition('brillant');
    setEventOD('Sans');
    setEventOG('Sans');
    setEarSide('');
    setSelectedOptions([]);
    setNotes('');
    setFournirBushing(false);
    setPerEarDiff(false);
    setFormeEmboutOG('');
    setFormeDemiEpaulementOG(false);
    setFormeEvideeOG(false);
    setMatiereOG('resine_3d');
    setDureteOG('40');
    setMontageType('');
    setObturateurPoignee(false);
    setObturateurCordon(false);
    setObturateurAttenuation('');
    setObturateurEtui(false);
    setObturateurFlottantRaw(false);
    setFinitionAnallergique(false);
    setMarquageSpecial(false);
    setMarquageSpecialTexte('');
    setIntegralCableLength('');
    setBteBrandSpecificEnabled(false);
    setBteBrandSpecificType('');
    setBteCordaMinifitDiameter('');
    setFiltreType('cerustop');
    setBague3dsaConsent(false);
    setBague3dsaFallback('');
    setEcouteurVersionWSA('');
    setEcouteurPuissanceWSA('');
    setEcouteurPuissanceWSAOG('');
    setConformateurDescription('');
    setConformateurPhotoIds([]);
  }, [clearSpeakerInfo]);

  marqueRef.current = marque;
  emboutTypeRef.current = emboutType;

  return {
    patientRef, patientType, deliveryDate, textureOreille,
    fabricationType, obturateurType, emboutType,
    marque, modele,
    ecouteurVersion, ecouteurPuissance,
    ecouteurType, ecouteurPuissanceOticon,
    ecouteurInfoComplementaire, infoComplementaire,
    formeEmbout, formeDemiEpaulement, formeEvidee, matiere, durete, couleur, etancheite, finition,
    eventOD, eventOG, earSide, notes, selectedOptions,
    fournirBushing,
    // Per-ear
    isBilateral, perEarDiff, formeEmboutOG, formeDemiEpaulementOG, formeEvideeOG, matiereOG, dureteOG,
    ecouteurPuissanceOG, ecouteurPuissanceOticonOG,
    // Montage
    montageType,
    // Obturateur
    obturateurPoignee, obturateurCordon, obturateurAttenuation, obturateurEtui, obturateurForme, obturateurFlottant,
    // Finition
    finitionAnallergique,
    // Marquage
    marquageSpecial, marquageSpecialTexte,
    marquageSpecialMode, marquageSpecialImageId,
    // Integral
    integralCableLength,
    // BTE brand-specific mini-tube
    bteBrandSpecificEnabled, bteBrandSpecificType, bteCordaMinifitDiameter,
    // Filtre choice (SlimTip Link & Intégral)
    filtreType,
    // Bague 3DSA
    bague3dsaConsent, bague3dsaFallback,
    // Résine souple sur au moins une oreille : non laquée, pas de marquage spécial
    isResineSouple: fabricationType === 'embout_auditif' && (matiere === 'resine_souple' || (earSide === 'both' && perEarDiff && matiereOG === 'resine_souple')),
    // Groupe WSA (Signia/Rexton/Biotone/Widex)
    ecouteurVersionWSA, ecouteurPuissanceWSA, ecouteurPuissanceWSAOG,
    // Conformateur d'oreille
    conformateurDescription, conformateurPhotoIds,

    setPatientRef, setPatientType, setDeliveryDate, setTextureOreille,
    setFabricationType, setObturateurType: setObturateurTypeWrapped, setEmboutType,
    setMarque, setModele,
    setEcouteurVersion, setEcouteurPuissance,
    setEcouteurType, setEcouteurPuissanceOticon,
    setEcouteurInfoComplementaire, setInfoComplementaire,
    setFormeEmbout, setFormeDemiEpaulement, setFormeEvidee, setMatiere, setDurete: setDureteWrapped, setCouleur: setCouleurWrapped, setEtancheite, setFinition,
    setEventOD, setEventOG, setEarSide, setNotes,
    setFournirBushing,
    setPerEarDiff, setFormeEmboutOG, setFormeDemiEpaulementOG, setFormeEvideeOG, setMatiereOG, setDureteOG: setDureteOGWrapped,
    setEcouteurPuissanceOG, setEcouteurPuissanceOticonOG,
    setMontageType,
    setObturateurPoignee, setObturateurCordon, setObturateurAttenuation, setObturateurEtui, setObturateurForme, setObturateurFlottant,
    setFinitionAnallergique,
    setMarquageSpecial, setMarquageSpecialTexte,
    setMarquageSpecialMode, setMarquageSpecialImageId,
    setIntegralCableLength,
    setBteBrandSpecificEnabled, setBteBrandSpecificType, setBteCordaMinifitDiameter,
    setFiltreType,
    setBague3dsaConsent, setBague3dsaFallback,
    setEcouteurVersionWSA, setEcouteurPuissanceWSA, setEcouteurPuissanceWSAOG,
    setConformateurDescription, setConformateurPhotoIds,

    resolvedProductKey, resolvedProduct, availableOptions,
    selectedOptionProducts, totalPrice,
    toggleOption, buildFormData, reset, loadFromOrder,
  };
}
